// Feature Gate System for Subscription Tier Management
// Controls access to premium features based on organization_tier



export type OrganizationTier = 'tier_1' | 'tier_2' | 'tier_3';

export interface TierFeatures {
  // Tier 1 (Basic) - Available to all
  coreRiskAssessments: boolean;
  basicReporting: boolean;
  riskRegistry: boolean;
  basicDashboard: boolean;
  
  // Tier 2 (Professional) - tier_2 and above
  executiveNarratives: boolean;
  departmentAnalytics: boolean;
  advancedFiltering: boolean;
  bulkEditing: boolean;
  
  // Tier 3 (Enterprise) - tier_3 only
  predictiveInsights: boolean;
  advancedExports: boolean;
  cmriTrends: boolean;
  maturityModeling: boolean;
  governanceMaturity: boolean;
  riskAppetiteConfig: boolean;
  advancedCertifications: boolean;
}

// Feature access matrix by tier
const TIER_FEATURES: Record<OrganizationTier, TierFeatures> = {
  tier_1: {
    coreRiskAssessments: true,
    basicReporting: true,
    riskRegistry: true,
    basicDashboard: true,
    executiveNarratives: false,
    departmentAnalytics: false,
    advancedFiltering: false,
    bulkEditing: false,
    predictiveInsights: false,
    advancedExports: false,
    cmriTrends: false,
    maturityModeling: false,
    governanceMaturity: false,
    riskAppetiteConfig: false,
    advancedCertifications: false,
  },
  tier_2: {
    coreRiskAssessments: true,
    basicReporting: true,
    riskRegistry: true,
    basicDashboard: true,
    executiveNarratives: true,
    departmentAnalytics: true,
    advancedFiltering: true,
    bulkEditing: true,
    predictiveInsights: false,
    advancedExports: false,
    cmriTrends: false,
    maturityModeling: false,
    governanceMaturity: false,
    riskAppetiteConfig: false,
    advancedCertifications: false,
  },
  tier_3: {
    coreRiskAssessments: true,
    basicReporting: true,
    riskRegistry: true,
    basicDashboard: true,
    executiveNarratives: true,
    departmentAnalytics: true,
    advancedFiltering: true,
    bulkEditing: true,
    predictiveInsights: true,
    advancedExports: true,
    cmriTrends: true,
    maturityModeling: true,
    governanceMaturity: true,
    riskAppetiteConfig: true,
    advancedCertifications: true,
  },
};

// Tier display information
export const TIER_INFO: Record<OrganizationTier, { name: string; color: string; bgColor: string; description: string }> = {
  tier_1: {
    name: 'Basic',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    description: 'Core risk assessments and basic reporting',
  },
  tier_2: {
    name: 'Professional',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    description: 'Executive narratives and department analytics',
  },
  tier_3: {
    name: 'Enterprise',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    description: 'Predictive insights and advanced exports',
  },
};

// Check if a feature is available for a given tier
export function hasFeatureAccess(
  tier: OrganizationTier | null | undefined,
  feature: keyof TierFeatures
): boolean {
  if (!tier) return false;

  const features = TIER_FEATURES[tier];
  if (!features) return false;

  return features[feature] || false;
}

/**
 * In-memory cache for tier features (client-side only)
 */
const tierFeaturesCache: Record<string, { features: any; timestamp: number }> = {};
const CACHE_TTL = 3600000; // 1 hour in milliseconds

/**
 * Get tier features with in-memory caching
 */
export async function getTierFeatures(tier: string) {
  // Check in-memory cache
  const cached = tierFeaturesCache[tier];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.features;
  }

  // Define tier features
  const tierFeatures = {
    tier_1: [
      'Up to 10 AI use cases',
      'Basic risk assessments',
      'Standard compliance mapping',
      '5 team members',
      'Email support',
    ],
    tier_2: [
      'Up to 50 AI use cases',
      'Advanced risk assessments',
      'Multi-jurisdiction compliance',
      '20 team members',
      'Priority support',
      'Custom controls library',
      'Audit trail export',
    ],
    tier_3: [
      'Unlimited AI use cases',
      'Enterprise risk intelligence',
      'Predictive analytics',
      'Unlimited team members',
      'Dedicated support',
      'Custom integrations',
      'White-label options',
      'API access',
    ],
  };

  const features = tierFeatures[tier as keyof typeof tierFeatures] || [];
  
  // Cache in memory
  tierFeaturesCache[tier] = {
    features,
    timestamp: Date.now(),
  };
  
  return features;
}

// Check if tier meets minimum requirement
export function meetsMinimumTier(
  currentTier: OrganizationTier | null | undefined,
  requiredTier: OrganizationTier
): boolean {
  if (!currentTier) return false;
  
  const tierOrder: OrganizationTier[] = ['tier_1', 'tier_2', 'tier_3'];
  const currentIndex = tierOrder.indexOf(currentTier);
  const requiredIndex = tierOrder.indexOf(requiredTier);
  
  return currentIndex >= requiredIndex;
}

// Get upgrade message for a feature
export function getUpgradeMessage(feature: keyof TierFeatures): string {
  const featureMessages: Record<keyof TierFeatures, string> = {
    coreRiskAssessments: 'Core risk assessments are available in all tiers.',
    basicReporting: 'Basic reporting is available in all tiers.',
    riskRegistry: 'Risk registry is available in all tiers.',
    basicDashboard: 'Basic dashboard is available in all tiers.',
    executiveNarratives: 'Executive narratives require Professional tier or higher.',
    departmentAnalytics: 'Department analytics require Professional tier or higher.',
    advancedFiltering: 'Advanced filtering requires Professional tier or higher.',
    bulkEditing: 'Bulk editing requires Professional tier or higher.',
    predictiveInsights: 'Predictive insights require Enterprise tier.',
    advancedExports: 'Advanced exports require Enterprise tier.',
    cmriTrends: 'CMRI trend analysis requires Enterprise tier.',
    maturityModeling: 'Maturity modeling requires Enterprise tier.',
    governanceMaturity: 'Governance maturity tracking requires Enterprise tier.',
    riskAppetiteConfig: 'Risk appetite configuration requires Enterprise tier.',
    advancedCertifications: 'Advanced certifications (ISO 42001, SOC 2, EU AI Act, ISO 27001) require Enterprise tier.',
  };
  
  return featureMessages[feature] || 'This feature requires a higher subscription tier.';
}

// Get required tier for a feature
export function getRequiredTier(feature: keyof TierFeatures): OrganizationTier | null {
  // Check tier_1 features
  if (TIER_FEATURES.tier_1[feature]) return 'tier_1';
  
  // Check tier_2 features
  if (TIER_FEATURES.tier_2[feature] && !TIER_FEATURES.tier_1[feature]) return 'tier_2';
  
  // Check tier_3 features
  if (TIER_FEATURES.tier_3[feature] && !TIER_FEATURES.tier_2[feature]) return 'tier_3';
  
  return null;
}